import { api } from './api';

export interface ConsultationSlot {
  start: string; // ISO string
  end: string; // ISO string
  available: boolean;
}

export interface AvailableSlotsResponse {
  date: string;
  slots: ConsultationSlot[];
}

export interface CreateConsultationData {
  consultationTypeId: string;
  name: string;
  email: string;
  phone?: string;
  experience?: string;
  goals?: string;
  budget?: string;
  location?: string;
  timeframe?: string;
  message?: string;
  scheduledDateTime: string; // ISO string
}

export interface Consultation {
  _id: string;
  userId?: string;
  consultationTypeId: string | {
    _id: string;
    name: string;
    duration: string;
    price: number;
    priceDisplay: string;
  };
  status: 'PENDING' | 'SCHEDULED' | 'COMPLETED' | 'CANCELLED';
  name: string;
  email: string;
  phone?: string;
  scheduledDateTime?: string;
  googleMeetLink?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

export const consultationService = {
  /**
   * Get available time slots for a specific date and consultation type
   */
  async getAvailableSlots(date: string, consultationTypeId: string): Promise<AvailableSlotsResponse> {
    try {
      const response = await api.get<ApiResponse<AvailableSlotsResponse>>('/consultations/slots/available', {
        params: {
          date,
          consultationTypeId,
        },
      });
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching available slots:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch available slots');
    }
  },

  /**
   * Book a consultation
   */
  async createConsultation(data: CreateConsultationData): Promise<Consultation> {
    try {
      const response = await api.post<ApiResponse<{ consultation: Consultation }>>('/consultations', data);
      return response.data.data.consultation;
    } catch (error: any) {
      console.error('Error creating consultation:', error);
      throw new Error(error.response?.data?.message || 'Failed to book consultation');
    }
  },

  /**
   * Get user's consultations (requires authentication)
   */
  async getMyConsultations(page: number = 1, limit: number = 10, status?: 'PENDING' | 'SCHEDULED' | 'COMPLETED' | 'CANCELLED'): Promise<{
    consultations: Consultation[];
    totalCount: number;
    currentPage: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  }> {
    try {
      const body: any = { page, limit };
      if (status) {
        body.status = status;
      }
      const response = await api.post<ApiResponse<{
        consultations: Consultation[];
        totalCount: number;
        currentPage: number;
        totalPages: number;
        hasNextPage: boolean;
        hasPrevPage: boolean;
      }>>('/v1/consultations/my', body);
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching consultations:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch consultations');
    }
  },

  /**
   * Get a single consultation
   */
  async getConsultation(id: string): Promise<Consultation> {
    try {
      const response = await api.get<ApiResponse<Consultation>>(`/consultations/${id}`);
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching consultation:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch consultation');
    }
  },

  /**
   * Cancel a consultation
   */
  async cancelConsultation(id: string): Promise<void> {
    try {
      await api.post(`/consultations/${id}/cancel`);
    } catch (error: any) {
      console.error('Error cancelling consultation:', error);
      throw new Error(error.response?.data?.message || 'Failed to cancel consultation');
    }
  },
};

